import BioSection from "components/Screens/Marketplace/Customer/ProfileSection/BioSection";
import GlanceSection from "components/Screens/Marketplace/Customer/ProfileSection/GlanceSection";
import OverviewSection from "components/Screens/Marketplace/Customer/ProfileSection/OverviewSection";
import HandsOnExperienceSection from "components/Screens/Marketplace/Customer/ProfileSection/HandsOnExperienceSection";
import SoftwareSkillsSection from "components/Screens/Marketplace/Customer/ProfileSection/SoftwareSkillsSection";
import CertificationsSection from "components/Screens/Marketplace/Customer/ProfileSection/CertificationsSection";
import WorkExperienceSection from "components/Screens/Marketplace/Customer/ProfileSection/WorkExperienceSection";
import EducationSection from "components/Screens/Marketplace/Customer/ProfileSection/EducationSection";

import {
  mapTalentToOverviewItems,
  mapTalentToWorkExperience,
  mapTalentToEducation,
  mapTalentToSoftwareSkills,
  mapTalentToSkillsChips,
  mapTalentToCertifications,
  generateBioFromTalent,
} from "components/Screens/Marketplace/Customer/utils/dataMappers";

import type { AiSummaryCapability, AiSummarySentiment, TalentProfile } from "components/Screens/Marketplace/Customer/types";

import "components/Screens/Marketplace/Customer/ProfileSection/ProfileContent.scss";

interface ProfileContentProps {
  talent: TalentProfile;
  /** "See how {name} works" opened (PLG glance_expanded). */
  onGlanceExpanded?: () => void;
  /** "This helps" / "Not quite" on the glance; resolves false when the vote was not saved. */
  onGlanceFeedback?: (sentiment: AiSummarySentiment, capabilities: AiSummaryCapability[]) => Promise<boolean>;
}

const glancePitch = (talent: TalentProfile) => talent.aiSummary?.recruiterPitch?.trim() || "";

export function getAvailableTabs(talent: TalentProfile) {
  const bio = generateBioFromTalent(talent);
  const bioText = Array.isArray(bio) ? bio.join(" ") : bio || "";
  const overviewItems = mapTalentToOverviewItems(talent);
  const handsOnExperience = mapTalentToSkillsChips(talent);
  const softwareSkills = mapTalentToSoftwareSkills(talent);
  const certifications = mapTalentToCertifications(talent);
  const workExperience = mapTalentToWorkExperience(talent);
  const education = mapTalentToEducation(talent);

  const tabs: { id: string; label: string }[] = [];

  if (glancePitch(talent)) tabs.push({ id: "glance", label: "At a glance" });
  if (bioText.trim()) tabs.push({ id: "bio", label: "Bio" });
  if (overviewItems.length > 0) tabs.push({ id: "overview", label: "Overview" });
  if (handsOnExperience.length > 0) tabs.push({ id: "hands-on", label: "Skills" });
  if (softwareSkills.length > 0) tabs.push({ id: "software", label: "Software skills" });
  if (certifications.length > 0) tabs.push({ id: "certifications", label: "Certifications" });
  if (workExperience.length > 0) tabs.push({ id: "work", label: "Work experience" });
  if (education.length > 0) tabs.push({ id: "education", label: "Education" });

  return tabs;
}

function ProfileContent({ talent, onGlanceExpanded, onGlanceFeedback }: ProfileContentProps) {
  const bio = generateBioFromTalent(talent);
  const bioText = Array.isArray(bio) ? bio.join(" ") : bio || "";
  const overviewItems = mapTalentToOverviewItems(talent);
  const handsOnExperience = mapTalentToSkillsChips(talent);
  const softwareSkills = mapTalentToSoftwareSkills(talent);
  const certifications = mapTalentToCertifications(talent);
  const workExperience = mapTalentToWorkExperience(talent);
  const education = mapTalentToEducation(talent);

  return (
    <div className="profile-content">
      {glancePitch(talent) && (
        <GlanceSection
          firstName={talent.firstName}
          pitch={glancePitch(talent)}
          snapshot={talent.aiSummary?.personalitySnapshot?.trim()}
          onExpand={onGlanceExpanded}
          vote={talent.aiSummaryFeedback?.recruiterPitch}
          onFeedback={onGlanceFeedback}
        />
      )}
      {bioText.trim() && <BioSection bio={bioText} />}
      {overviewItems.length > 0 && <OverviewSection overviewItems={overviewItems} />}
      {handsOnExperience.length > 0 && <HandsOnExperienceSection experience={handsOnExperience} />}
      {softwareSkills.length > 0 && <SoftwareSkillsSection skills={softwareSkills} />}
      {certifications.length > 0 && <CertificationsSection certifications={certifications} />}
      {workExperience.length > 0 && <WorkExperienceSection experience={workExperience} />}
      {education.length > 0 && <EducationSection education={education} />}
    </div>
  );
}

export default ProfileContent;
